/**
 * Apply a data-sda-event attribute to an anchor element to have its click
 * events automatically tracked.
 *
 * ShiftDigital already tracks its own events via a script included on the page,
 * but this hook allows us to track them in our own analytics system as well.
 */
import { useEffect, useState } from 'react';
import { useTrackEvent } from '~/context/common/AnalyticsProvider';
import { useDealershipId } from '~/context/dealership/StaticContext';

export default function useTrackShiftDigitalClickEvent() {
  const [previousEvent, setPreviousEvent] = useState({
    previousEventId: '',
    previousTime: {} as Date
  });
  const dealershipId = useDealershipId();
  const trackEvent = useTrackEvent();

  useEffect(() => {
    function onClick(ev: MouseEvent) {
      if (!(ev.target instanceof Element)) return;

      const target = ev.target.closest<HTMLElement>('[data-sda-event]');
      const { sdaEvent, sdEventSetProp } = target?.dataset || {};

      if (!sdaEvent) return;

      const { previousEventId, previousTime } = previousEvent;

      // Prevent double-clicks sending multiple events
      if (
        sdaEvent === previousEventId &&
        Date.now() - previousTime.getTime() < 500
      ) {
        setPreviousEvent({
          previousEventId: sdaEvent,
          previousTime: new Date()
        });

        return;
      }

      setPreviousEvent({
        previousEventId: sdaEvent,
        previousTime: new Date()
      });

      // Convert clickToCallDepartment|Sales to { clickToCallDepartment: 'Sales' }
      const eventProps = sdEventSetProp
        ? Object.fromEntries([sdEventSetProp.split('|')])
        : {};

      const context = {
        dealershipId,
        event: sdaEvent,
        ...eventProps
      };

      trackEvent('ShiftDigital', context);
    }

    document.addEventListener('click', onClick, true);
    document.addEventListener('contextmenu', onClick, true);

    return () => {
      document.removeEventListener('click', onClick);
      document.removeEventListener('contextmenu', onClick);
    };
  }, [dealershipId, previousEvent, trackEvent]);
}
